import { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Check, ExternalLink, X } from "lucide-react";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TaxAccountApiClient } from "@/clients/taxAccountApiClient";
import { heldForDays, taxBookkeepingStatus, type TaxRowBookkeeping } from "./taxBookkeepingStatus";
import { ResolveHeldTaxRowDialog } from "./ResolveHeldTaxRowDialog";

/**
 * FIN-1167 EC8: a private row the consultant believes is a business cost. Not supported yet, so
 * the press only records the request (FIN-1336) and says so; the row itself never changes.
 */
function PrivateRowStatus({ transactionId }: { transactionId: string }) {
  const t = useTranslations("consultantTax.bookkeepingStatus");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reported, setReported] = useState(false);
  // State updates are async, so a fast double click could pass an `isSubmitting` check twice.
  const inFlight = useRef(false);

  const report = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    try {
      await TaxAccountApiClient.reportPrivateRowBusiness(transactionId);
      setReported(true);
    } catch {
      // The API client has already toasted the failure with its reference; the button re-enables.
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant="outline">{t("privateBadge")}</Badge>
      {reported ? (
        <p className="text-xs text-muted-foreground" data-testid="tax-private-row-business-note">
          {t("businessNote")}
        </p>
      ) : (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs text-muted-foreground"
          disabled={isSubmitting}
          onClick={() => void report()}
        >
          {t("businessItem")}
        </Button>
      )}
    </div>
  );
}

/**
 * FIN-1617 AC10: a deposit or payout with no bank transaction yet. It says why it is not booked,
 * how long it has waited, and offers the consultant's one way out: a dialog with two choices and a
 * mandatory note.
 */
function AwaitingBankMatchStatus({
  transactionId,
  heldAt,
  onResolved,
}: {
  transactionId: string;
  heldAt: string | null | undefined;
  onResolved: () => void | Promise<void>;
}) {
  const t = useTranslations("consultantTax.bookkeepingStatus");
  const [open, setOpen] = useState(false);
  const heldDays = heldForDays(heldAt, new Date());
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant="outline">{t("awaitingBankMatch")}</Badge>
      {heldDays !== null && (
        <p className="text-xs text-muted-foreground" data-testid="tax-held-for">
          {t("heldForWaiting", { days: heldDays })}
        </p>
      )}
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        data-testid="tax-resolve-open"
        onClick={() => setOpen(true)}
      >
        {t("resolve")}
      </Button>
      <ResolveHeldTaxRowDialog transactionId={transactionId} open={open} onOpenChange={setOpen} onResolved={onResolved} />
    </div>
  );
}

/** The tax account page's Bookkeeping cell, shaped like the bank page's match status. */
export function TaxBookkeepingStatusCell({
  transactionId,
  bookkeeping,
  onResolved,
}: {
  transactionId: string;
  bookkeeping: TaxRowBookkeeping | undefined;
  /** Reloads the page after a consultant resolved a held row (FIN-1617). */
  onResolved?: () => void | Promise<void>;
}) {
  const t = useTranslations("consultantTax.bookkeepingStatus");
  const status = taxBookkeepingStatus(bookkeeping);
  switch (status.kind) {
    case "review":
      return (
        <div className="flex items-center">
          <Check className="h-4 w-4 text-green-500 mr-2" aria-hidden="true" />
          <RouterLink to={status.href} className="text-blue-500 hover:underline flex items-center">
            {t("viewInReview")}
            <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
          </RouterLink>
        </div>
      );
    case "outside_books":
      return <PrivateRowStatus transactionId={transactionId} />;
    case "held_sie_overlap":
      return <Badge variant="outline">{t("heldSieOverlap")}</Badge>;
    case "held_payment_bypassed_1630":
      return <Badge variant="outline">{t("heldBypassed1630")}</Badge>;
    case "closed_fiscal_year":
      return <Badge variant="outline">{t("closedFiscalYear")}</Badge>;
    case "awaiting_bank_match":
      return (
        <AwaitingBankMatchStatus
          transactionId={transactionId}
          heldAt={bookkeeping?.heldAt}
          onResolved={onResolved ?? (() => undefined)}
        />
      );
    case "resolved_already_booked":
      return <Badge variant="outline">{t("resolvedAlreadyBooked")}</Badge>;
    case "resolved_paid_from_outside":
      return <Badge variant="outline">{t("resolvedPaidFromOutside")}</Badge>;
    case "none":
      return <X className="h-4 w-4 text-red-500" aria-label={t("none")} role="img" />;
  }
}
